/**
 * MyTaskDetailPanel
 *
 * Wrapper used by MyTasksPage when the user clicks "View details" on a task row.
 *
 * Decision logic:
 *  - Red Dot workflow task → opens the rich TaskDetailPanel.
 *  - All other tasks (standalone, preference_assessment, unknown workflow type) →
 *    fall back to the simpler TaskDetailModal.
 *
 * Why preference_assessment is excluded: TaskDetailPanel form renderers submit via
 * `/api/v1/red-dot/tasks/:id/form*`, which is guarded by `red_dot:execute`. Routing
 * PA tasks through that panel would 403 for users who hold only PA permissions.
 *
 * Why GET /api/v1/red-dot/:id is NOT called here: that endpoint auto-transitions
 * SUBMITTED → UNDER_REVIEW for the assigned clinician. Calling it merely to hydrate
 * the panel would mutate workflow state as a side effect of opening a task detail.
 * The basic clientMember shape (id/firstName/lastName) is passed instead; extended
 * client fields (dateOfBirth, gender, etc.) will not auto-populate in form renderers
 * when the panel is opened from My Tasks — this is an accepted trade-off.
 *
 * TaskDetailPanel is lazy-loaded to keep pdfjs-dist out of the My Tasks bundle
 * and to avoid JSDOM import failures in the test suite (pdfjs requires browser
 * globals like DOMMatrix that are absent in Node).
 *
 * Note: TaskDetailPanel internally reads `(workflowInstance as any).client?.id`
 * for PA sub-task creation. The `client` field below mirrors the pattern used
 * by RedDotDetailPage until TaskDetailPanel is updated to read `clientMember` directly.
 *
 * formDefinitionCode and statusReason are mapped from the task-engine task detail
 * endpoint (useTask) via taskToRedDotTask. submissionData is NOT fetched to avoid
 * expanding every task list response with large PHI blobs.
 */

import { lazy, Suspense } from 'react';
import styled from 'styled-components';
import { useTask } from '../../hooks/useTasks';
import { TaskDetailModal } from '../../components/tasks';
import { taskToRedDotTask } from '../../utils/taskMapping';
import type { TaskDetailPanelProps } from '../RedDotDetailPage/TaskDetailPanel';

// Lazy-loaded to defer pdfjs-dist initialization until the panel is actually needed.
const TaskDetailPanel = lazy<React.ComponentType<TaskDetailPanelProps>>(
  () => import('../RedDotDetailPage/TaskDetailPanel').then((m) => ({ default: m.TaskDetailPanel })),
);

// ============================================================================
// Props
// ============================================================================

export interface MyTaskDetailPanelProps {
  taskId: string;
  onClose: () => void;
}

// ============================================================================
// Styled components
// ============================================================================

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.5);
  z-index: ${({ theme }) => theme.zIndex.modal};
  cursor: pointer;
`;

const OverlayMessage = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.backgrounds.white};
  cursor: default;
`;

const ErrorCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[3]};
  padding: ${({ theme }) => theme.spacing[6]};
  background: ${({ theme }) => theme.colors.backgrounds.white};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  box-shadow: ${({ theme }) => theme.shadows.lg};
  cursor: default;
`;

const ErrorText = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.status.error.text};
  margin: 0;
`;

const DismissButton = styled.button`
  padding: ${({ theme }) => `${theme.spacing[2]} ${theme.spacing[4]}`};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-family: inherit;
  color: ${({ theme }) => theme.colors.text.primary};
  background: ${({ theme }) => theme.colors.backgrounds.gray};
  border: 1px solid ${({ theme }) => theme.colors.borders.medium};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  cursor: pointer;

  &:hover {
    background: ${({ theme }) => theme.colors.backgrounds.page};
  }
`;

// ============================================================================
// Component
// ============================================================================

export function MyTaskDetailPanel({ taskId, onClose }: MyTaskDetailPanelProps) {
  const { data: task, isLoading, isError } = useTask(taskId);

  if (isLoading) {
    // Clicking the backdrop dismisses the loading overlay so a hung request
    // does not trap the user on the page.
    return (
      <Overlay onClick={onClose}>
        <OverlayMessage onClick={(e) => e.stopPropagation()}>
          Loading task…
        </OverlayMessage>
      </Overlay>
    );
  }

  if (isError || !task) {
    return (
      <Overlay onClick={onClose}>
        <ErrorCard onClick={(e) => e.stopPropagation()}>
          <ErrorText>Failed to load task. Please try again.</ErrorText>
          <DismissButton type="button" onClick={onClose}>
            Dismiss
          </DismissButton>
        </ErrorCard>
      </Overlay>
    );
  }

  const { workflowInstanceId, workflowInstance } = task;

  // Only Red Dot workflow tasks go through TaskDetailPanel. All other tasks
  // (standalone, unknown workflow types) fall back to TaskDetailModal.
  //
  // `preference_assessment` tasks inside a red_dot workflow ARE included here because
  // TaskDetailPanel renders the "Start/Continue Preference Assessment" action for
  // that code — without it, the clinician cannot launch the assessment from My Tasks.
  // The action uses paService (PA API), not the Red Dot form endpoints.
  if (!workflowInstanceId || !workflowInstance || workflowInstance.workflowType !== 'red_dot') {
    // Pass the pre-fetched task as initialData so TaskDetailModal doesn't trigger
    // a second GET /tasks/:id fetch. The data is already in the React Query cache
    // from the useTask() call above; this prevents the second observer from
    // scheduling a redundant background refetch (default staleTime is 0).
    return <TaskDetailModal taskId={taskId} onClose={onClose} initialData={task} />;
  }

  return (
    <Suspense
      fallback={
        <Overlay onClick={onClose}>
          <OverlayMessage onClick={(e) => e.stopPropagation()}>
            Loading task…
          </OverlayMessage>
        </Overlay>
      }
    >
      <TaskDetailPanel
        task={taskToRedDotTask(task)}
        workflowInstanceId={workflowInstanceId}
        workflowInstance={{
          id: workflowInstance.id,
          workflowType: workflowInstance.workflowType,
          status: workflowInstance.status,
          slaDeadline: workflowInstance.slaDeadline,
          clientMember: workflowInstance.clientMember,
          // `client` mirrors the pattern in RedDotDetailPage: TaskDetailPanel and form
          // renderers read (workflowInstance as any).client for client info.
          // Extended client fields (dateOfBirth, gender, etc.) are unavailable here;
          // fetching GET /api/v1/red-dot/:id would auto-transition SUBMITTED → UNDER_REVIEW.
          client: workflowInstance.clientMember,
          assignedTo: workflowInstance.assignedTo,
          // submissionData is included from the task detail endpoint (absent on list endpoint).
          // SchemaFormRenderer reads submissionData.option and .goalText for CTA Red Dot tasks.
          submissionData: workflowInstance.submissionData,
        } as any}
        onClose={onClose}
        onAction={onClose}
      />
    </Suspense>
  );
}

export default MyTaskDetailPanel;
